// Generated by `python manage.py export_permissions` in scripay-middleware. Do not edit by hand.

export const PERMISSIONS = {
  /** See roles, their permissions and who holds them (External, medium risk) */
  RoleView: 'role.view',
  /** Create, edit and delete roles and change their permissions (External, critical risk) */
  RoleManage: 'role.manage',
  /** Give roles to users and take them away (External, critical risk) */
  RoleAssign: 'role.assign',
  /** Approve or reject requests for critical access (Internal, critical risk) */
  RoleApprove: 'role.approve',
  /** See the access log and the access review (External, medium risk) */
  AuditView: 'audit.view',
  /** See merchant profiles (External, low risk) */
  MerchantView: 'merchant.view',
  /** Edit merchant profiles (External, high risk) */
  MerchantUpdate: 'merchant.update',
  /** Activate or deactivate a merchant (Internal, critical risk) */
  MerchantSuspend: 'merchant.suspend',
  /** See onboarding applications and their status (External, low risk) */
  ApplicationView: 'application.view',
  /** Approve or reject onboarding applications (Internal, high risk) */
  ApplicationReview: 'application.review',
  /** See KYC documents (External, medium risk) */
  KycView: 'kyc.view',
  /** Upload and replace KYC documents (External, medium risk) */
  KycSubmit: 'kyc.submit',
  /** Approve KYC documents (Internal, high risk) */
  KycApprove: 'kyc.approve',
  /** See staff members and invites (External, low risk) */
  StaffView: 'staff.view',
  /** Invite staff and cancel or resend invites (External, high risk) */
  StaffInvite: 'staff.invite',
  /** Edit staff members (External, medium risk) */
  StaffUpdate: 'staff.update',
  /** Activate or deactivate staff members (External, high risk) */
  StaffDeactivate: 'staff.deactivate',
  /** See the transaction types a merchant may use (External, low risk) */
  ScopeView: 'scope.view',
  /** Switch transaction types on or off for a merchant (Internal, critical risk) */
  ScopeAssign: 'scope.assign',
  /** See mobile money credentials (External, medium risk) */
  MobileCredentialView: 'mobile_credential.view',
  /** Add and edit mobile money credentials (External, critical risk) */
  MobileCredentialManage: 'mobile_credential.manage',
  /** See SMS providers (External, low risk) */
  SmsProviderView: 'sms_provider.view',
  /** Add and edit SMS providers (External, medium risk) */
  SmsProviderManage: 'sms_provider.manage',
  /** See SMS templates (External, low risk) */
  SmsTemplateView: 'sms_template.view',
  /** Add and edit SMS templates (External, medium risk) */
  SmsTemplateManage: 'sms_template.manage',
  /** See webhook urls (External, low risk) */
  WebhookView: 'webhook.view',
  /** Add, edit and remove webhook urls (External, high risk) */
  WebhookManage: 'webhook.manage',
  /** See fees (External, low risk) */
  FeeView: 'fee.view',
  /** Configure fees (External, high risk) */
  FeeManage: 'fee.manage',
  /** See how many approvals a payment needs (External, low risk) */
  ApprovalLevelView: 'approval_level.view',
  /** Change how many approvals a payment needs (External, high risk) */
  ApprovalLevelManage: 'approval_level.manage',
  /** See API credentials (External, medium risk) */
  CredentialView: 'credential.view',
  /** Generate, share and change API credentials (External, critical risk) */
  CredentialManage: 'credential.manage',
  /** See transactions, ledgers, messages and callbacks (External, low risk) */
  TransactionView: 'transaction.view',
  /** See payments and transfers (External, low risk) */
  PaymentView: 'payment.view',
  /** Create payments and transfers (External, high risk) */
  PaymentInitiate: 'payment.initiate',
  /** Approve or reject payments (External, critical risk) */
  PaymentApprove: 'payment.approve',
  /** See bills (External, low risk) */
  BillView: 'bill.view',
  /** Pay bills (External, high risk) */
  BillPay: 'bill.pay',
  /** See wallets, managers, alerts and sweeps (External, low risk) */
  WalletView: 'wallet.view',
  /** Create and edit wallets, alerts and sweeps (External, high risk) */
  WalletManage: 'wallet.manage',
  /** Suspend or reactivate a wallet (Internal, critical risk) */
  WalletSuspend: 'wallet.suspend',
  /** Set up the wallets for a country (Internal, critical risk) */
  WalletSetup: 'wallet.setup',
  /** See wallet profiles (External, low risk) */
  ProfileView: 'profile.view',
  /** Create and edit wallet profiles (External, medium risk) */
  ProfileManage: 'profile.manage',
  /** Verify wallet profiles (External, high risk) */
  ProfileVerify: 'profile.verify',
  /** See reports and statements (External, low risk) */
  ReportView: 'report.view',
  /** Choose the columns on statements (External, low risk) */
  ReportConfigure: 'report.configure',
  /** See fee reports and merchant rankings (Internal, medium risk) */
  FeeReportView: 'fee_report.view',
  /** See notification settings (External, low risk) */
  NotificationView: 'notification.view',
  /** Change notification settings (External, low risk) */
  NotificationManage: 'notification.manage',
  /** See statements, corrections and the calendar (Internal, medium risk) */
  ReconciliationView: 'reconciliation.view',
  /** Upload statements, propose corrections and lock days (Internal, critical risk) */
  ReconciliationSettle: 'reconciliation.settle',
  /** Approve or reject corrections (Internal, critical risk) */
  ReconciliationApprove: 'reconciliation.approve',
  /** See merchant settlements (Internal, medium risk) */
  SettlementView: 'settlement.view',
  /** Record merchant settlements a provider made (Internal, high risk) */
  SettlementRequest: 'settlement.request',
  /** Approve, confirm, cancel or reject merchant settlements (Internal, critical risk) */
  SettlementApprove: 'settlement.approve',
} as const;

export type PermissionName = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Permissions on which the API asks for a one-time code first (Scripay staff). */
export const CRITICAL_PERMISSIONS: readonly PermissionName[] = [
  PERMISSIONS.RoleManage,
  PERMISSIONS.RoleAssign,
  PERMISSIONS.RoleApprove,
  PERMISSIONS.MerchantSuspend,
  PERMISSIONS.ScopeAssign,
  PERMISSIONS.MobileCredentialManage,
  PERMISSIONS.CredentialManage,
  PERMISSIONS.PaymentApprove,
  PERMISSIONS.WalletSuspend,
  PERMISSIONS.WalletSetup,
  PERMISSIONS.ReconciliationSettle,
  PERMISSIONS.ReconciliationApprove,
  PERMISSIONS.SettlementApprove,
];

export interface PermissionInfo {
  access: 'Internal' | 'External';
  risk: 'Low' | 'Medium' | 'High' | 'Critical';
  description: string;
}

/** What each permission allows, for role editors. Internal ones are for Scripay staff only. */
export const PERMISSION_INFO: Record<PermissionName, PermissionInfo> = {
  'role.view': { access: 'External', risk: 'Medium', description: 'See roles, their permissions and who holds them' },
  'role.manage': { access: 'External', risk: 'Critical', description: 'Create, edit and delete roles and change their permissions' },
  'role.assign': { access: 'External', risk: 'Critical', description: 'Give roles to users and take them away' },
  'role.approve': { access: 'Internal', risk: 'Critical', description: 'Approve or reject requests for critical access' },
  'audit.view': { access: 'External', risk: 'Medium', description: 'See the access log and the access review' },
  'merchant.view': { access: 'External', risk: 'Low', description: 'See merchant profiles' },
  'merchant.update': { access: 'External', risk: 'High', description: 'Edit merchant profiles' },
  'merchant.suspend': { access: 'Internal', risk: 'Critical', description: 'Activate or deactivate a merchant' },
  'application.view': { access: 'External', risk: 'Low', description: 'See onboarding applications and their status' },
  'application.review': { access: 'Internal', risk: 'High', description: 'Approve or reject onboarding applications' },
  'kyc.view': { access: 'External', risk: 'Medium', description: 'See KYC documents' },
  'kyc.submit': { access: 'External', risk: 'Medium', description: 'Upload and replace KYC documents' },
  'kyc.approve': { access: 'Internal', risk: 'High', description: 'Approve KYC documents' },
  'staff.view': { access: 'External', risk: 'Low', description: 'See staff members and invites' },
  'staff.invite': { access: 'External', risk: 'High', description: 'Invite staff and cancel or resend invites' },
  'staff.update': { access: 'External', risk: 'Medium', description: 'Edit staff members' },
  'staff.deactivate': { access: 'External', risk: 'High', description: 'Activate or deactivate staff members' },
  'scope.view': { access: 'External', risk: 'Low', description: 'See the transaction types a merchant may use' },
  'scope.assign': { access: 'Internal', risk: 'Critical', description: 'Switch transaction types on or off for a merchant' },
  'mobile_credential.view': { access: 'External', risk: 'Medium', description: 'See mobile money credentials' },
  'mobile_credential.manage': { access: 'External', risk: 'Critical', description: 'Add and edit mobile money credentials' },
  'sms_provider.view': { access: 'External', risk: 'Low', description: 'See SMS providers' },
  'sms_provider.manage': { access: 'External', risk: 'Medium', description: 'Add and edit SMS providers' },
  'sms_template.view': { access: 'External', risk: 'Low', description: 'See SMS templates' },
  'sms_template.manage': { access: 'External', risk: 'Medium', description: 'Add and edit SMS templates' },
  'webhook.view': { access: 'External', risk: 'Low', description: 'See webhook urls' },
  'webhook.manage': { access: 'External', risk: 'High', description: 'Add, edit and remove webhook urls' },
  'fee.view': { access: 'External', risk: 'Low', description: 'See fees' },
  'fee.manage': { access: 'External', risk: 'High', description: 'Configure fees' },
  'approval_level.view': { access: 'External', risk: 'Low', description: 'See how many approvals a payment needs' },
  'approval_level.manage': { access: 'External', risk: 'High', description: 'Change how many approvals a payment needs' },
  'credential.view': { access: 'External', risk: 'Medium', description: 'See API credentials' },
  'credential.manage': { access: 'External', risk: 'Critical', description: 'Generate, share and change API credentials' },
  'transaction.view': { access: 'External', risk: 'Low', description: 'See transactions, ledgers, messages and callbacks' },
  'payment.view': { access: 'External', risk: 'Low', description: 'See payments and transfers' },
  'payment.initiate': { access: 'External', risk: 'High', description: 'Create payments and transfers' },
  'payment.approve': { access: 'External', risk: 'Critical', description: 'Approve or reject payments' },
  'bill.view': { access: 'External', risk: 'Low', description: 'See bills' },
  'bill.pay': { access: 'External', risk: 'High', description: 'Pay bills' },
  'wallet.view': { access: 'External', risk: 'Low', description: 'See wallets, managers, alerts and sweeps' },
  'wallet.manage': { access: 'External', risk: 'High', description: 'Create and edit wallets, alerts and sweeps' },
  'wallet.suspend': { access: 'Internal', risk: 'Critical', description: 'Suspend or reactivate a wallet' },
  'wallet.setup': { access: 'Internal', risk: 'Critical', description: 'Set up the wallets for a country' },
  'profile.view': { access: 'External', risk: 'Low', description: 'See wallet profiles' },
  'profile.manage': { access: 'External', risk: 'Medium', description: 'Create and edit wallet profiles' },
  'profile.verify': { access: 'External', risk: 'High', description: 'Verify wallet profiles' },
  'report.view': { access: 'External', risk: 'Low', description: 'See reports and statements' },
  'report.configure': { access: 'External', risk: 'Low', description: 'Choose the columns on statements' },
  'fee_report.view': { access: 'Internal', risk: 'Medium', description: 'See fee reports and merchant rankings' },
  'notification.view': { access: 'External', risk: 'Low', description: 'See notification settings' },
  'notification.manage': { access: 'External', risk: 'Low', description: 'Change notification settings' },
  'reconciliation.view': { access: 'Internal', risk: 'Medium', description: 'See statements, corrections and the calendar' },
  'reconciliation.settle': { access: 'Internal', risk: 'Critical', description: 'Upload statements, propose corrections and lock days' },
  'reconciliation.approve': { access: 'Internal', risk: 'Critical', description: 'Approve or reject corrections' },
  'settlement.view': { access: 'Internal', risk: 'Medium', description: 'See merchant settlements' },
  'settlement.request': { access: 'Internal', risk: 'High', description: 'Record merchant settlements a provider made' },
  'settlement.approve': { access: 'Internal', risk: 'Critical', description: 'Approve, confirm, cancel or reject merchant settlements' },
};

/** Old names still returned by my-permissions, and what each became. */
export const LEGACY_PERMISSIONS: Record<string, readonly PermissionName[]> = {
  'Manage Roles': [PERMISSIONS.RoleView, PERMISSIONS.RoleManage, PERMISSIONS.RoleAssign, PERMISSIONS.AuditView],
  'Manage Merchant': [PERMISSIONS.MerchantUpdate],
  'Manage Staff': [PERMISSIONS.StaffInvite, PERMISSIONS.StaffUpdate, PERMISSIONS.StaffDeactivate],
  'Manage Merchant Application': [PERMISSIONS.ApplicationReview],
  'Manage KYC': [PERMISSIONS.KycSubmit, PERMISSIONS.KycApprove],
  'Assign Scope': [PERMISSIONS.ScopeAssign],
  'Manage Scopes': [PERMISSIONS.ScopeView],
  'Manage Mobile Credentials': [PERMISSIONS.MobileCredentialManage],
  'Manage Sms Providers': [PERMISSIONS.SmsProviderManage],
  'Manage Sms Template': [PERMISSIONS.SmsTemplateManage],
  'Initiate Payment': [PERMISSIONS.PaymentInitiate, PERMISSIONS.BillPay],
  'Approve Payment': [PERMISSIONS.PaymentApprove],
  'Manage Wallets': [PERMISSIONS.WalletManage, PERMISSIONS.WalletSuspend, PERMISSIONS.WalletSetup],
  'Manage Profiles': [PERMISSIONS.ProfileManage],
  'Verify Profiles': [PERMISSIONS.ProfileVerify],
  'Manage Notifications': [PERMISSIONS.NotificationManage],
  'Settle Transactions': [PERMISSIONS.ReconciliationSettle, PERMISSIONS.ReconciliationApprove],
};
